import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Crown, Loader2, Check, X, CreditCard, Smartphone, ShoppingBag,
  AlertTriangle, RefreshCw, ArrowUpCircle, History,
  Activity, Sparkles, Trophy, Lightbulb,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const SubscriptionSection = ({
  subscription,
  tiers,
  checkingPayment,
  selectedTier,
  setSelectedTier,
  paymentMethod,
  setPaymentMethod,
  paypalClientId,
  showCancelConfirm,
  setShowCancelConfirm,
  cancellingSubscription,
  changingTier,
  paymentHistory,
  headers,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  onChangeTier,
  onCancelTierChange,
}) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
    <Card className={`glass ${subscription?.is_premium ? 'border-moon-gold/30' : ''}`}>
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Crown className="w-5 h-5 text-moon-gold" />
          Subscription
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {checkingPayment && (
          <div className="flex items-center gap-2 text-mystic-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            Activating your subscription...
          </div>
        )}

        <div className="glass-light rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">
              {subscription?.is_premium ? "Premium" : "Free"} Plan
            </span>
            <Badge variant={subscription?.is_premium ? "default" : "outline"} className={subscription?.is_premium ? "bg-moon-gold text-mystic-900" : ""}>
              {subscription?.status || "Active"}
            </Badge>
          </div>
          {subscription?.expires_at && (
            <p className="text-mystic-200/70 text-sm">
              Expires: {new Date(subscription.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {!subscription?.is_premium && (
          <>
            {/* Premium pillars highlight */}
            <div className="rounded-2xl border border-moon-gold/30 bg-gradient-to-br from-moon-gold/10 via-amber-500/5 to-moon-gold/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-moon-gold" />
                <p className="text-moon-gold text-xs uppercase tracking-widest font-semibold">What you'll unlock</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2.5" data-testid="premium-pillar-kick-counter">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                    <Activity className="w-4 h-4 text-pink-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-tight">Kick Counter</p>
                    <p className="text-mystic-200/70 text-xs mt-0.5">Track baby's activity in trimester 3</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5" data-testid="premium-pillar-baby-names">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-purple-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-tight">Baby Name Generator</p>
                    <p className="text-mystic-200/70 text-xs mt-0.5">AI-curated names with origins & favorites</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5" data-testid="premium-pillar-milestones">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-amber-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-tight">Pregnancy Milestones</p>
                    <p className="text-mystic-200/70 text-xs mt-0.5">Heartbeat, viability, full-term & more</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5" data-testid="premium-pillar-conception-tips">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-rose-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-tight">Conception Tips</p>
                    <p className="text-mystic-200/70 text-xs mt-0.5">Evidence-based fertility guidance</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <p className="text-mystic-200/70 text-xs flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-green-400" /> Detailed natal chart & yearly forecast
                </p>
                <p className="text-mystic-200/70 text-xs flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-green-400" /> Unlimited AI tips & journal insights
                </p>
                <p className="text-mystic-200/70 text-xs flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-green-400" /> Personalized rituals & exclusive content
                </p>
                <p className="text-mystic-200/70 text-xs flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-green-400" /> Priority support
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className={`glass-light rounded-xl p-4 cursor-pointer transition-all ${selectedTier === 'premium_monthly' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                onClick={() => setSelectedTier('premium_monthly')}
              >
                <h4 className="text-white font-medium mb-2">Premium Monthly</h4>
                <p className="text-2xl font-bold text-moon-gold mb-2">$11.33<span className="text-sm text-mystic-200/70">/mo</span></p>
                <ul className="space-y-1 mb-4">
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> All 4 premium tools above</li>
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Detailed natal chart</li>
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Personalized rituals</li>
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Unlimited AI tips</li>
                </ul>
              </div>

              <div
                className={`glass-light rounded-xl p-4 cursor-pointer transition-all ${selectedTier === 'premium_yearly' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                onClick={() => setSelectedTier('premium_yearly')}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">Premium Yearly</h4>
                  <Badge className="bg-green-500/20 text-green-400">Save 33%</Badge>
                </div>
                <p className="text-2xl font-bold text-moon-gold mb-2">$91.09<span className="text-sm text-mystic-200/70">/yr</span></p>
                <ul className="space-y-1 mb-4">
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Everything in Monthly</li>
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Yearly forecast</li>
                  <li className="text-mystic-200/70 text-sm flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Exclusive content</li>
                </ul>
              </div>
            </div>

            {selectedTier && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4 mt-6"
              >
                <h4 className="text-white font-medium">Choose Payment Method</h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={`glass-light rounded-xl p-3 text-center transition-all ${paymentMethod === 'stripe' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                  >
                    <CreditCard className="w-6 h-6 mx-auto mb-1 text-mystic-300" />
                    <span className="text-white text-xs">Card</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('apple_pay')}
                    className={`glass-light rounded-xl p-3 text-center transition-all ${paymentMethod === 'apple_pay' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                  >
                    <Smartphone className="w-6 h-6 mx-auto mb-1 text-mystic-300" />
                    <span className="text-white text-xs">Apple Pay</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('google_pay')}
                    className={`glass-light rounded-xl p-3 text-center transition-all ${paymentMethod === 'google_pay' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                  >
                    <ShoppingBag className="w-6 h-6 mx-auto mb-1 text-mystic-300" />
                    <span className="text-white text-xs">Google Pay</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypal')}
                    className={`glass-light rounded-xl p-3 text-center transition-all ${paymentMethod === 'paypal' ? 'border-2 border-moon-gold' : 'border border-transparent hover:border-mystic-500/50'}`}
                  >
                    <svg className="w-6 h-6 mx-auto mb-1" viewBox="0 0 24 24" fill="#00457C">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.217a.779.779 0 0 1 .768-.654h6.19c2.045 0 3.674.548 4.674 1.588.943.98 1.276 2.377.961 4.032-.012.063-.025.126-.037.19-.443 2.254-1.673 3.757-3.594 4.414-.812.277-1.726.408-2.743.408H8.61a.779.779 0 0 0-.768.654l-1.158 6.14a.641.641 0 0 1-.633.54h-.975z" />
                      <path d="M22.132 7.063c-.015.077-.031.155-.048.234-.618 3.136-2.73 4.773-6.104 4.773h-1.556a.947.947 0 0 0-.936.8l-.8 5.056-.226 1.434a.5.5 0 0 0 .494.578h3.465a.779.779 0 0 0 .768-.654l.032-.163.61-3.87.04-.214a.779.779 0 0 1 .768-.654h.483c3.13 0 5.58-1.272 6.296-4.95.3-1.535.145-2.818-.647-3.72a3.02 3.02 0 0 0-.64-.65z" fill="#0079C1" />
                    </svg>
                    <span className="text-white text-xs">PayPal</span>
                  </button>
                </div>

                <div className="mt-4">
                  {paymentMethod === 'paypal' && paypalClientId ? (
                    <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD" }}>
                      <PayPalButtons
                        style={{ layout: "horizontal", color: "gold", shape: "pill" }}
                        createOrder={async () => {
                          try {
                            const res = await axios.post(`${API}/paypal/create-order`, {
                              tier_id: selectedTier,
                              origin_url: window.location.origin
                            }, { headers, withCredentials: true });
                            window.location.href = res.data.approval_url;
                            return res.data.payment_id;
                          } catch (error) {
                            toast.error("Failed to create PayPal order");
                            throw error;
                          }
                        }}
                        onError={() => {
                          toast.error("PayPal error occurred");
                        }}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <Button
                      onClick={() => onSubscribe(selectedTier)}
                      className="w-full bg-moon-gold hover:bg-moon-gold/90 text-mystic-900 rounded-full py-6"
                      data-testid="subscribe-btn"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      {paymentMethod === 'apple_pay' ? 'Pay with Apple Pay' :
                       paymentMethod === 'google_pay' ? 'Pay with Google Pay' :
                       `Subscribe - $${selectedTier === 'premium_yearly' ? '91.09' : '11.33'}`}
                    </Button>
                  )}

                  <p className="text-center text-mystic-400 text-xs mt-3">
                    Secure payment powered by Stripe & PayPal
                  </p>

                  <div className="flex items-center justify-center gap-3 mt-3 opacity-60">
                    <span className="text-mystic-400 text-xs">Visa</span>
                    <span className="text-mystic-400 text-xs">Mastercard</span>
                    <span className="text-mystic-400 text-xs">Amex</span>
                    <span className="text-mystic-400 text-xs">Discover</span>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        {subscription?.is_premium && (
          <div className="space-y-4">
            {subscription?.status === 'cancelled' && (
              <div className="glass-light rounded-xl p-4 border border-amber-500/30 bg-amber-500/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-300 font-medium">Subscription Cancelled</p>
                    <p className="text-mystic-200/70 text-sm mt-1">
                      Your premium access will end on {new Date(subscription.expires_at).toLocaleDateString()}
                    </p>
                    <Button
                      onClick={onReactivateSubscription}
                      className="mt-3 bg-moon-gold hover:bg-moon-gold/90 text-mystic-900"
                      size="sm"
                      data-testid="reactivate-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reactivate Subscription
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {subscription?.pending_tier_change && (
              <div className="glass-light rounded-xl p-4 border border-blue-500/30 bg-blue-500/10">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <ArrowUpCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-300 font-medium">
                        {subscription.tier_change_type === 'upgrade' ? 'Upgrade' : 'Downgrade'} Scheduled
                      </p>
                      <p className="text-mystic-200/70 text-sm mt-1">
                        Changing to {tiers[subscription.pending_tier_change]?.name || subscription.pending_tier_change} on {new Date(subscription.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={onCancelTierChange}
                    variant="ghost"
                    size="sm"
                    className="text-mystic-300 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {subscription?.status !== 'cancelled' && !subscription?.pending_tier_change && (
              <div className="glass-light rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-mystic-300" />
                  Change Plan
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {subscription?.tier !== 'premium_monthly' && (
                    <Button
                      onClick={() => onChangeTier('premium_monthly')}
                      disabled={changingTier}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-mystic-700"
                      data-testid="change-to-monthly-btn"
                    >
                      {changingTier ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Monthly ($11.33/mo)'}
                    </Button>
                  )}
                  {subscription?.tier !== 'premium_yearly' && (
                    <Button
                      onClick={() => onChangeTier('premium_yearly')}
                      disabled={changingTier}
                      variant="outline"
                      className="border-moon-gold/30 text-moon-gold hover:bg-moon-gold/10"
                      data-testid="change-to-yearly-btn"
                    >
                      {changingTier ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yearly ($91.09/yr)'}
                    </Button>
                  )}
                </div>
                <p className="text-mystic-400 text-xs mt-2">Changes take effect at next billing date</p>
              </div>
            )}

            {paymentHistory.length > 0 && (
              <div className="glass-light rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-mystic-300" />
                  Payment History
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {paymentHistory.slice(0, 5).map((payment, idx) => (
                    <div key={`k-${idx}`} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-0">
                      <div>
                        <span className="text-white">{tiers[payment.tier_id]?.name || payment.tier_id}</span>
                        <span className="text-mystic-400 text-xs ml-2">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-mystic-200">${payment.amount}</span>
                        <Badge
                          variant="outline"
                          className={payment.status === 'completed' ? 'border-green-500/30 text-green-400' : 'border-amber-500/30 text-amber-400'}
                        >
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {subscription?.status !== 'cancelled' && (
              <div className="pt-4 border-t border-white/10">
                {!showCancelConfirm ? (
                  <Button
                    onClick={() => setShowCancelConfirm(true)}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Subscription
                  </Button>
                ) : (
                  <div className="glass-light rounded-xl p-4 border border-red-500/30 bg-red-500/10">
                    <p className="text-red-300 font-medium mb-2">Are you sure you want to cancel?</p>
                    <p className="text-mystic-200/70 text-sm mb-4">
                      You'll retain premium access until {new Date(subscription.expires_at).toLocaleDateString()}, then your account will revert to the free plan.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={onCancelSubscription}
                        disabled={cancellingSubscription}
                        className="bg-red-600 hover:bg-red-500 text-white"
                        data-testid="confirm-cancel-btn"
                      >
                        {cancellingSubscription ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Yes, Cancel
                      </Button>
                      <Button
                        onClick={() => setShowCancelConfirm(false)}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-mystic-700"
                      >
                        Keep Subscription
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

export default SubscriptionSection;
